import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaUser, FaBuilding, FaCalendarAlt, FaKey, FaShieldAlt, FaCreditCard, FaGlobe, FaMapMarkerAlt, FaBriefcase } from 'react-icons/fa';

interface MemberEditPageProps {
  userId: number;
  onBack: () => void;
}

const MemberEditPage: React.FC<MemberEditPageProps> = ({ userId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [membersList, setMembersList] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    number: '',
    DOB: '',
    Time: '', // Joining Date
    expire: '', // Expiry Date
    role: 'member',
    sub_role: '',
    company_name: '',
    business_classification: '',
    designation: '',
    induction_by: '',
    company_address: '',
    company_website: '',
    about_company: '',
    social: '',
    status: 'active',
    payment_details: '',
    profile: ''
  });
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileFormData = new FormData();
    fileFormData.append('image', file);
    setIsUploadingPhoto(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.imagePath) {
        setFormData((prev: any) => ({
          ...prev,
          profile: res.data.imagePath
        }));
      }
    } catch (err) {
      console.error('Failed to upload member profile image:', err);
      alert('Failed to upload profile photo.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const formatDateForInput = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchMemberData = async () => {
      setLoading(true);
      try {
        const [userRes, rolesRes, membersRes] = await Promise.all([
          axios.get(`https://rmbbackend.rmbjaipur.com/auth/user/${userId}`),
          axios.get(`https://rmbbackend.rmbjaipur.com/auth/roles`).catch(() => ({ data: { roles: [] } })),
          axios.get(`https://rmbbackend.rmbjaipur.com/auth/allusersinfo`).catch(() => ({ data: [] }))
        ]);

        if (rolesRes.data && rolesRes.data.roles) {
          setAvailableRoles(rolesRes.data.roles);
        }
        if (membersRes.data) {
          setMembersList(Array.isArray(membersRes.data) ? membersRes.data : []);
        }

        const data = Array.isArray(userRes.data) ? userRes.data[0] : userRes.data;
        if (data) {
          setFormData({
            name: data.name || '',
            email: data.email || '',
            password: data.plain_password || data.password || '',
            number: data.number || '',
            DOB: formatDateForInput(data.DOB),
            Time: formatDateForInput(data.Time),
            expire: formatDateForInput(data.expire),
            role: data.role || 'member',
            sub_role: data.sub_role || '',
            company_name: data.company_name || '',
            business_classification: data.business_classification || '',
            designation: data.designation || 'RMBian',
            induction_by: data.induction_by || '',
            company_address: data.company_address || '',
            company_website: data.company_website || '',
            about_company: data.about_company || '',
            social: data.social || '',
            status: data.status || 'active',
            payment_details: data.payment_details || '',
            profile: data.profile || ''
          });
        }
      } catch (err) {
        console.error('Error fetching member details for edit:', err);
        alert('Failed to load member details.');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [userId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${userId}`, {
        ...formData,
        imagePath: formData.profile
      });
      alert('Member Profile & Credentials updated successfully!');
      onBack();
    } catch (err) {
      console.error('Error updating member:', err);
      alert('Failed to update member profile.');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-gray-600 font-bold text-base">Loading member profile editor...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Top Header */}
      <div className="bg-white p-5 rounded-xl shadow-sm border mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg shadow-sm transition flex items-center gap-2 font-bold text-xs"
          >
            <FaArrowLeft /> Back to Roster
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              ✏️ Member Profile & Credentials Editor
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">Editing Profile for Member ID: #{userId}</p>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-xs rounded-lg shadow transition"
        >
          <FaSave /> Save Changes
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Row 1: Credentials & Lifecycle Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Account Credentials */}
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center gap-2">
              <FaKey className="text-[#FDBC12]" /> User ID & Security Credentials
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">User ID / Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Account Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full p-2.5 pr-10 border border-gray-300 rounded-lg outline-none font-mono font-bold text-blue-900"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-800"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Phone / Mobile Number</label>
                <input
                  type="text"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                />
              </div>
            </div>
          </div>

          {/* Card 2: Lifecycle Dates */}
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center gap-2">
              <FaCalendarAlt className="text-emerald-600" /> Membership Lifecycle & Birthday Dates
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">📅 Date of Joining (Tenure Start)</label>
                <input
                  type="date"
                  name="Time"
                  value={formData.Time}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-emerald-800"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">⏳ Date of Membership Expiry</label>
                <input
                  type="date"
                  name="expire"
                  value={formData.expire}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-amber-800"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">🎂 Date of Birth (Birthday)</label>
                <input
                  type="date"
                  name="DOB"
                  value={formData.DOB}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Profile Picture Upload */}
        <div className="bg-white p-5 rounded-xl shadow-sm border">
          <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2">🖼️ Member Profile Photo & Avatar</span>
            {isUploadingPhoto && <span className="text-xs text-amber-600 font-bold animate-pulse">Uploading profile photo...</span>}
          </h2>

          <div className="flex flex-col sm:flex-row items-center gap-6 p-2">
            <div className="w-24 h-24 rounded-full overflow-hidden border-3 border-[#FDBC12] shadow-lg flex-shrink-0 bg-gray-100 flex items-center justify-center p-1 bg-[#0A2451]">
              {formData.profile ? (
                <img
                  src={formData.profile.startsWith('http') ? formData.profile : `https://rmbbackend.rmbjaipur.com/${formData.profile.replace(/\\/g, '/')}`}
                  alt="Profile Avatar"
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/150?text=Member'; }}
                />
              ) : (
                <span className="text-xs text-slate-300 font-bold text-center">No Photo</span>
              )}
            </div>

            <div className="w-full space-y-2 text-xs">
              <label className="font-bold text-gray-700 block">Upload New Profile Picture (JPG, PNG, WEBP)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full text-xs text-gray-700 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#0A2451] file:text-white hover:file:bg-blue-900 cursor-pointer border border-gray-300 rounded-lg p-1.5"
              />
              {formData.profile && (
                <p className="text-[11px] text-emerald-700 font-mono font-semibold truncate bg-emerald-50 p-2 rounded border border-emerald-200">
                  ✓ Active Profile Image Path: {formData.profile}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Row 3: Personal & Business Info */}
        <div className="bg-white p-5 rounded-xl shadow-sm border">
          <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center gap-2">
            <FaBuilding className="text-blue-600" /> Member Personal & Business Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs mb-4">
            <div>
              <label className="font-bold text-gray-700 block mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                required
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 block mb-1">Company Name</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 block mb-1">Business Classification</label>
              <input
                type="text"
                name="business_classification"
                value={formData.business_classification}
                onChange={handleChange}
                placeholder="e.g. IT Services, Real Estate, Healthcare"
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs mb-4">
            <div>
              <label className="font-bold text-gray-700 block mb-1">Business Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                placeholder="e.g. Director, Founder, CEO"
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 block mb-1">Induction By (Inducted Member)</label>
              <select
                name="induction_by"
                value={formData.induction_by}
                onChange={handleChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-blue-900 bg-blue-50/30"
              >
                <option value="">-- Select Inducting Member --</option>
                {membersList.map(m => (
                  <option key={m.id} value={m.name}>{m.name} ({m.company_name || 'Member'})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs mb-4">
            <div>
              <label className="font-bold text-gray-700 block mb-1">Company Address</label>
              <input
                type="text"
                name="company_address"
                value={formData.company_address}
                onChange={handleChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 block mb-1">Company Website</label>
              <input
                type="text"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
              />
            </div>
          </div>

          <div className="text-xs mb-4">
            <label className="font-bold text-gray-700 block mb-1">About Company / Business Description</label>
            <textarea
              name="about_company"
              value={formData.about_company}
              onChange={handleChange}
              rows={2}
              className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
            />
          </div>

          <div className="text-xs">
            <label className="font-bold text-gray-700 block mb-1">Social Media Links / Handles</label>
            <input
              type="text"
              name="social"
              value={formData.social}
              onChange={handleChange}
              placeholder="LinkedIn, Instagram or Facebook URL"
              className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
            />
          </div>
        </div>

        {/* Row 3: Roles, Status & Payment Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center gap-2">
              <FaShieldAlt className="text-purple-600" /> System Role & Sub-Role Designation
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">System Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                >
                  <option value="member">Regular RMB Member</option>
                  <option value="chapter-admin">Chapter Admin</option>
                  <option value="super-admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Position / Designation Sub-Role</label>
                <select
                  name="sub_role"
                  value={formData.sub_role}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                >
                  <option value="">No Specific Position</option>
                  {availableRoles.map(r => {
                    const fullTitle = r.sub_category ? `${r.role_name} - ${r.sub_category}` : r.role_name;
                    return (
                      <option key={r.id} value={fullTitle}>{fullTitle}</option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h2 className="text-sm font-bold text-[#0A2451] border-b pb-3 mb-4 flex items-center gap-2">
              <FaCreditCard className="text-amber-600" /> Account Status & Payment Details
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">Account Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                >
                  <option value="active">🟢 Active</option>
                  <option value="pending">⏳ Pending Verification (Self-Reg)</option>
                  <option value="inactive">🛑 Inactive</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Payment Details / Receipt Notes</label>
                <input
                  type="text"
                  name="payment_details"
                  value={formData.payment_details}
                  onChange={handleChange}
                  placeholder="e.g. Annual Dues Paid via UPI Ref #90123"
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Save Bar */}
        <div className="flex justify-end gap-3 bg-white p-4 rounded-xl border shadow-sm">
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 font-bold text-xs rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-xs rounded-lg shadow flex items-center gap-2"
          >
            <FaSave /> Save Member Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default MemberEditPage;
